/**
 * Shaft Loading Dynamics — 3-segment lumped mass ODE model.
 *
 * Replaces the static lookup tables (kick point → launch offset) with a
 * physics simulation of how the shaft actually bends during the downswing.
 *
 * Model: 3 masses connected by spring-dampers, driven by hand kinematics.
 *
 *   HAND ──[k₁,c₁]── BUTT ──[k₂,c₂]── MID ──[k₃,c₃]── TIP+HEAD
 *
 * The ODE runs at 240 Hz over a ~0.3s downswing (~72 steps).
 * Output: tip deflection, kick timing, closure rate → launch/spin/speed/dispersion.
 *
 * EI Profile: 5-point bending stiffness curve [tip, 25%, 50%, 75%, butt] in N·m².
 * This is the real differentiator between shafts — not just "stiff" or "regular".
 *
 * Performance: <0.5ms per simulation (72 RK4 steps on 6 state variables).
 */

import type { Tempo, Transition } from '../types';

// ── EI Profile Type ──────────────────────────────────────────────────────────

export interface ShaftEIProfile {
  /** Bending stiffness at 5 points along shaft (tip=0 → butt=4).
   *  Units: N·m² (bending rigidity EI = Young's modulus × second moment of area).
   *  Typical graphite driver shaft: 15–60 N·m² range.
   *  Typical steel iron shaft: 20–50 N·m² range. */
  ei: [number, number, number, number, number];

  /** Material damping ratio (dimensionless).
   *  Carbon fiber ≈ 0.015–0.025 (low loss, snappy feel).
   *  Steel ≈ 0.008–0.015 (very low loss, firm feel). */
  dampingRatio: number;

  /** Tip section mass in grams (includes adapter, ~8-15g). */
  tipMass: number;

  /** Butt section mass in grams (includes grip, ~45-55g). */
  buttMass: number;

  /** Torsional stiffness in N·m/rad.
   *  Inversely related to torque rating.
   *  Low torque (2.5°) ≈ 1.8 N·m/rad.  High torque (5.5°) ≈ 0.8 N·m/rad. */
  torsionalStiffness: number;
}

// ── Swing Kinematics ─────────────────────────────────────────────────────────

export interface SwingKinematics {
  /** Peak hand speed in m/s (derived from club speed). */
  peakHandSpeed: number;

  /** Normalized timing of peak hand speed (0–1 over downswing).
   *  Fast tempo ≈ 0.60–0.70, slow tempo ≈ 0.75–0.85. */
  peakTimingNorm: number;

  /** Normalized wrist release point (0–1).
   *  Early release ≈ 0.45–0.55, late release ≈ 0.70–0.80. */
  releasePointNorm: number;

  /** Wrist release rate in rad/s (how fast wrists unhinge).
   *  Aggressive: 25–35 rad/s. Smooth: 15–22 rad/s. */
  releaseRate: number;

  /** Transition force multiplier (0–1).
   *  Smooth transition ≈ 0.25–0.40. Violent ≈ 0.75–0.95. */
  transitionForce: number;
}

// ── Shaft Delivery Output ────────────────────────────────────────────────────

export interface ShaftDelivery {
  /** Tip speed bonus from kick (m/s). Well-timed: +1 to +2 m/s. Mistimed: 0 to -0.5. */
  tipSpeedBonus: number;

  /** Dynamic loft addition from shaft droop (degrees). Typically +0.5° to +3.5°. */
  dynamicLoftAdd: number;

  /** Shaft lean at impact (degrees). Negative = forward lean = delofting.
   *  Typically -2° to +1°. */
  shaftLean: number;

  /** Face closure rate from torsion (degrees/sec). Higher = more face closing. */
  closureRate: number;

  /** Kick timing quality (0–1). 1.0 = perfect timing, peak tip speed at impact.
   *  <0.7 = significant mistiming, lost energy. */
  kickTimingQuality: number;

  /** Delivery consistency (0–1). Higher = more repeatable delivery.
   *  Affected by how sensitive the shaft is to input variations. */
  deliveryConsistency: number;
}

// ── Shaft Loading Modifiers (what the EquipmentEngine consumes) ──────────────

export interface ShaftLoadingResult {
  /** Launch angle delta from dynamic loft + shaft lean (degrees). */
  launchDelta: number;
  /** Spin multiplier from shaft lean effect (forward lean = less spin). */
  spinMult: number;
  /** Ball speed multiplier from kick timing + tip speed bonus. */
  speedMult: number;
  /** Dispersion multiplier from delivery consistency + torsion. */
  dispersionMult: number;
  /** Raw delivery data for diagnostics. */
  delivery: ShaftDelivery;
}

// ── Constants ────────────────────────────────────────────────────────────────

const DT = 1 / 240;              // 240 Hz, matches physics timestep
const DOWNSWING_DURATION = 0.30;  // seconds (typical tour: 0.25–0.35s)
const STEPS = Math.round(DOWNSWING_DURATION / DT);  // ~72 steps

// Segment lengths as fraction of total shaft (tip=0.33, mid=0.34, butt=0.33)
const SEG_TIP = 0.33;
const SEG_MID = 0.34;
const SEG_BUTT = 0.33;

// ── EI → Spring Constants ────────────────────────────────────────────────────

/**
 * Convert 5-point EI profile to 3 spring constants (one per segment).
 * Each spring constant k = EI_avg / L³ for that segment (beam bending stiffness).
 * L is segment length in meters.
 */
function eiToSprings(ei: [number, number, number, number, number], shaftLengthM: number): [number, number, number] {
  // Average EI for each segment
  const eiTip = (ei[0] + ei[1]) / 2;     // tip + 25%
  const eiMid = (ei[1] + ei[2] + ei[3]) / 3;  // 25% + 50% + 75%
  const eiButt = (ei[3] + ei[4]) / 2;    // 75% + butt

  const lTip = SEG_TIP * shaftLengthM;
  const lMid = SEG_MID * shaftLengthM;
  const lButt = SEG_BUTT * shaftLengthM;

  // k = 3*EI / L³ (cantilever beam stiffness) with lumped model scaling.
  // The lumped mass model needs softer springs than a real cantilever to match
  // the first-mode bending frequency of a continuous beam. Scale factor of 0.02
  // tunes the natural frequency to ~2-4 Hz → half-period matches the ~150ms
  // that the shaft needs to go from backward bend to forward kick.
  const LUMPED_SCALE = 0.06;
  return [
    LUMPED_SCALE * 3 * eiButt / (lButt * lButt * lButt),
    LUMPED_SCALE * 3 * eiMid / (lMid * lMid * lMid),
    LUMPED_SCALE * 3 * eiTip / (lTip * lTip * lTip),
  ];
}

/**
 * Compute damping coefficients from spring constants and damping ratio.
 * c = 2 * dampingRatio * sqrt(k * m)  (critical damping formula)
 */
function computeDamping(
  springs: [number, number, number],
  masses: [number, number, number],
  dampingRatio: number,
): [number, number, number] {
  return [
    2 * dampingRatio * Math.sqrt(springs[0] * masses[0]),
    2 * dampingRatio * Math.sqrt(springs[1] * masses[1]),
    2 * dampingRatio * Math.sqrt(springs[2] * masses[2]),
  ];
}

// ── Hand Acceleration Profile ────────────────────────────────────────────────

/**
 * Compute hand acceleration at normalized time t (0–1) over downswing.
 *
 * Three phases:
 *   1. Transition (0→15%): Ramp up. Hands pull forward, head lags → shaft bows back.
 *   2. Sustained pull (15%→release): Smooth buildup toward peak.
 *   3. Wrist release (release→100%): Sharp acceleration burst drives the kick.
 *
 * The peak timing (controlled by tempo) determines when the acceleration
 * maximum occurs. Fast tempo = peak at ~63% of downswing (early release).
 * Slow tempo = peak at ~80% (late release, shaft stays loaded longer).
 */
function handAccelProfile(t: number, swing: SwingKinematics): number {
  const peakAccel = swing.peakHandSpeed / (DOWNSWING_DURATION * 0.3);

  // Phase 1: Transition — ramp from zero. Violence affects steepness.
  if (t < 0.15) {
    const rampUp = t / 0.15;
    const transitionScale = 0.3 + swing.transitionForce * 0.7;
    return peakAccel * rampUp * transitionScale;
  }

  // Phase 2: Sustained pull — smooth buildup
  if (t < swing.releasePointNorm) {
    const pullPhase = (t - 0.15) / Math.max(0.01, swing.releasePointNorm - 0.15);
    const sineRamp = Math.sin(pullPhase * Math.PI * 0.5);
    return peakAccel * (0.5 + 0.5 * sineRamp * sineRamp);
  }

  // Phase 3: Wrist release — sharp peak near impact
  const releasePhase = (t - swing.releasePointNorm) / Math.max(0.01, 1.0 - swing.releasePointNorm);
  const releaseSigma = Math.max(0.1, 30 / swing.releaseRate);
  const peakOffset = Math.max(0, (swing.peakTimingNorm - swing.releasePointNorm) / Math.max(0.01, 1.0 - swing.releasePointNorm));
  const releaseAccel = Math.exp(-(((releasePhase - peakOffset) ** 2)) / (2 * releaseSigma * releaseSigma));

  return peakAccel * (0.6 + 1.4 * releaseAccel);
}

// ── RK4 Integrator for 6-state system ────────────────────────────────────────

type State6 = [number, number, number, number, number, number];

function derivatives(
  state: State6,
  handAccel: number,
  k: [number, number, number],
  c: [number, number, number],
  m: [number, number, number],
): State6 {
  const [x1, v1, x2, v2, x3, v3] = state;

  // Forces on butt mass (driven by hand)
  const f1 = handAccel * m[0] - k[0] * x1 - c[0] * v1 + k[1] * (x2 - x1) + c[1] * (v2 - v1);
  // Forces on mid mass
  const f2 = -k[1] * (x2 - x1) - c[1] * (v2 - v1) + k[2] * (x3 - x2) + c[2] * (v3 - v2);
  // Forces on tip mass (+ head mass)
  const f3 = -k[2] * (x3 - x2) - c[2] * (v3 - v2);

  return [v1, f1 / m[0], v2, f2 / m[1], v3, f3 / m[2]];
}

function rk4Step(
  state: State6,
  dt: number,
  handAccel: number,
  k: [number, number, number],
  c: [number, number, number],
  m: [number, number, number],
): State6 {
  const add = (a: State6, b: State6, scale: number): State6 =>
    a.map((v, i) => v + b[i] * scale) as State6;

  const k1 = derivatives(state, handAccel, k, c, m);
  const k2 = derivatives(add(state, k1, dt / 2), handAccel, k, c, m);
  const k3 = derivatives(add(state, k2, dt / 2), handAccel, k, c, m);
  const k4 = derivatives(add(state, k3, dt), handAccel, k, c, m);

  return state.map((v, i) =>
    v + (dt / 6) * (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i])
  ) as State6;
}

// ── Main Simulation ──────────────────────────────────────────────────────────

/**
 * Simulate shaft loading dynamics over the downswing.
 *
 * @param eiProfile - Shaft EI profile (5-point stiffness + damping + mass)
 * @param swing - Player swing kinematics
 * @param headMassKg - Club head mass in kg (driver ~0.200, iron ~0.260)
 * @param shaftLengthInches - Shaft length in inches
 * @returns ShaftDelivery — tip conditions at impact
 */
export function simulateShaftLoading(
  eiProfile: ShaftEIProfile,
  swing: SwingKinematics,
  headMassKg: number,
  shaftLengthInches: number,
): ShaftDelivery {
  const shaftLengthM = shaftLengthInches * 0.0254;

  // Convert EI profile to spring constants
  const springs = eiToSprings(eiProfile.ei, shaftLengthM);

  // Mass distribution (convert grams to kg)
  const tipMassKg = eiProfile.tipMass / 1000 + headMassKg;
  const midMassKg = (eiProfile.buttMass + eiProfile.tipMass) / 2000;  // mid section ≈ average
  const buttMassKg = eiProfile.buttMass / 1000;
  const masses: [number, number, number] = [buttMassKg, midMassKg, tipMassKg];

  // Damping
  const damping = computeDamping(springs, masses, eiProfile.dampingRatio);

  // Integrate
  let state: State6 = [0, 0, 0, 0, 0, 0];
  const tipHistory: number[] = [];
  const tipVelHistory: number[] = [];

  for (let i = 0; i < STEPS; i++) {
    const t = i / STEPS;
    const accel = handAccelProfile(t, swing);
    state = rk4Step(state, DT, accel, springs, damping, masses);
    tipHistory.push(state[4]);     // tip deflection
    tipVelHistory.push(state[5]);  // tip velocity
  }

  // ── Extract impact conditions from final state ──

  // Tip velocity at impact — positive means tip is still moving forward (kick ongoing)
  const tipVelAtImpact = state[5];

  // Find peak tip velocity (the "kick") and track sign changes for timing
  let peakTipVel = -Infinity;
  let peakTipIdx = 0;
  for (let i = 0; i < tipVelHistory.length; i++) {
    if (tipVelHistory[i] > peakTipVel) {
      peakTipVel = tipVelHistory[i];
      peakTipIdx = i;
    }
  }

  // Kick timing quality: how close is peak tip velocity to impact?
  // Perfect: peak at impact (last frame) = 1.0
  // Poor: peak was >25% of downswing before impact = 0.0
  const timingOffset = Math.abs(peakTipIdx - (STEPS - 1)) / STEPS;
  const kickTimingQuality = Math.max(0, 1.0 - timingOffset * 4.0);

  // Tip speed bonus from kick: tip velocity at impact adds head speed
  // Positive tip velocity = kick is still active at impact = good
  // Scale: ~1 m/s tip vel ≈ 0.5 m/s head speed bonus
  const tipSpeedBonus = Math.max(-0.5, tipVelAtImpact * 0.5);

  // ── Dynamic loft: tip TRAJECTORY angle at impact ──
  // Instead of using raw deflection (which is inverted for stiff-tip shafts),
  // use the tip velocity direction. A positive tip velocity at impact means
  // the tip is moving forward → adding dynamic loft (the "kick").
  // A negative or zero tip velocity means the kick already happened.
  //
  // This correctly models: low-kick shaft kicks at impact → high dynamic loft
  //                        high-kick shaft has already kicked → low dynamic loft
  //
  // atan2(tipVel, handSpeed) gives the angular deflection rate,
  // which directly maps to dynamic loft addition.
  const handSpeedAtImpact = swing.peakHandSpeed * 0.9;  // ~90% of peak at impact
  const kickAngle = Math.atan2(tipVelAtImpact, handSpeedAtImpact) * (180 / Math.PI);
  // Scale: a positive kick angle of ~1-3° is typical
  const dynamicLoftAdd = Math.max(0, kickAngle * 0.7);  // scale and floor at 0

  // Shaft lean: overall deflection pattern determines effective loft
  // Forward lean (butt ahead of tip) reduces effective loft
  const tipDeflection = state[4];
  const buttDeflection = state[0];
  const shaftLean = (buttDeflection - tipDeflection) * 8;  // degrees

  // Face closure rate from torsional stiffness
  const handTorque = swing.peakHandSpeed * 0.15;
  const closureRate = handTorque / Math.max(0.3, eiProfile.torsionalStiffness) * 100;

  // Delivery consistency: stiffer shafts + lower torque = more repeatable
  const avgEI = eiProfile.ei.reduce((a, b) => a + b, 0) / 5;
  const stiffnessScore = Math.min(1, avgEI / 50);
  const torqueScore = Math.min(1, eiProfile.torsionalStiffness / 2.0);
  const deliveryConsistency = 0.5 + 0.25 * stiffnessScore + 0.25 * torqueScore;

  return {
    tipSpeedBonus: Math.max(-0.5, Math.min(2.5, tipSpeedBonus)),
    dynamicLoftAdd: Math.max(0, Math.min(4.0, dynamicLoftAdd)),
    shaftLean: Math.max(-3, Math.min(2, shaftLean)),
    closureRate: Math.max(200, Math.min(600, closureRate)),
    kickTimingQuality: Math.max(0, Math.min(1, kickTimingQuality)),
    deliveryConsistency: Math.max(0.4, Math.min(1.0, deliveryConsistency)),
  };
}

// ── Convert ShaftDelivery → Equipment Modifiers ──────────────────────────────

/**
 * Convert raw shaft delivery into equipment modifier deltas.
 *
 * Hybrid approach:
 *   - Launch & spin: derived from EI profile shape (tip softness ratio).
 *     The ODE's tip deflection differences are too small to drive 2° launch spreads
 *     at 72-step resolution. Instead, use the EI imbalance (tip vs butt stiffness)
 *     which directly encodes how much dynamic loft the shaft adds.
 *   - Speed: from ODE kick timing quality (tempo × shaft interaction).
 *     This is where the ODE excels — it models when the tip energy arrives.
 *   - Dispersion: from delivery consistency + torsional stiffness.
 */
export function deliveryToModifiers(
  delivery: ShaftDelivery,
  baseline: ShaftDelivery,
  eiProfile: ShaftEIProfile,
  baselineEI: ShaftEIProfile,
): ShaftLoadingResult {
  // ── Launch: EI-derived tip softness ratio ──
  // Softer tip relative to butt = more dynamic loft = higher launch.
  // Compute tip/butt stiffness ratio; values <1 = soft tip, >1 = stiff tip.
  const tipButtRatio = (eiProfile.ei[0] + eiProfile.ei[1]) / (eiProfile.ei[3] + eiProfile.ei[4]);
  const baseTipButtRatio = (baselineEI.ei[0] + baselineEI.ei[1]) / (baselineEI.ei[3] + baselineEI.ei[4]);
  // Softer tip = lower ratio = more launch. Scale: 0.5 ratio difference ≈ 2° launch.
  const launchDelta = (baseTipButtRatio - tipButtRatio) * 4.0;

  // ── Spin: tip softness + kick timing interaction ──
  // Softer tip adds more dynamic loft at impact = more spin.
  // Kick timing also contributes: well-timed kick at impact adds loft = more spin.
  const tipSoftEffect = (baseTipButtRatio - tipButtRatio) * 0.08;
  const timingSpinEffect = (delivery.kickTimingQuality - baseline.kickTimingQuality) * 0.02;
  const spinMult = 1.0 + tipSoftEffect + timingSpinEffect;

  // ── Speed: ODE kick timing (the key tempo/transition effect) ──
  // Well-timed kick adds speed. Mistimed loses it.
  // tipSpeedBonus already accounts for tip velocity × quality
  const speedDelta = (delivery.tipSpeedBonus - baseline.tipSpeedBonus) * 0.004;
  // Kick timing quality directly: better timing = more efficient energy transfer
  const timingDelta = (delivery.kickTimingQuality - baseline.kickTimingQuality) * 0.018;
  const speedMult = 1.0 + speedDelta + timingDelta;

  // ── Dispersion: delivery consistency + torsion ──
  const consistencyRatio = baseline.deliveryConsistency / Math.max(0.4, delivery.deliveryConsistency);
  const closureDelta = (delivery.closureRate - baseline.closureRate) * 0.0003;
  const dispersionMult = consistencyRatio * (1.0 - closureDelta);

  return {
    launchDelta: Math.max(-2, Math.min(4, launchDelta)),
    spinMult: Math.max(0.90, Math.min(1.10, spinMult)),
    speedMult: Math.max(0.96, Math.min(1.04, speedMult)),
    dispersionMult: Math.max(0.70, Math.min(1.35, dispersionMult)),
    delivery,
  };
}

// ── Convert ShaftProfile → EI Profile ────────────────────────────────────────

/**
 * Derive an approximate EI profile from the existing ShaftProfile data.
 *
 * This bridges the current shaft DNA (kickPoint, tipStiffness, buttStiffness, torque)
 * to the new 5-point EI curve. Allows existing library entries to use the loading
 * model without needing measured EI data.
 *
 * When real EI data is available (from frequency profiling or manufacturer data),
 * it should be used directly instead.
 */
export function shaftProfileToEI(
  profile: { kickPoint: string; torque: number; tipStiffness: number; buttStiffness: number },
  weight: number,
  material: 'graphite' | 'steel',
): ShaftEIProfile {
  // Base EI from material and weight
  // Heavier shafts are generally stiffer
  const weightFactor = weight / 65;  // normalize to 65g reference
  const materialBase = material === 'steel' ? 35 : 28;  // steel is stiffer
  const baseEI = materialBase * weightFactor;

  // Kick point shapes the EI distribution
  // Low kick: soft tip, stiff butt → energy releases at tip
  // High kick: stiff tip, soft butt → energy stored longer
  const kickShapes: Record<string, [number, number, number, number, number]> = {
    'low':      [0.45, 0.60, 0.85, 1.15, 1.30],  // very soft tip → stiff butt (max kick)
    'mid':      [0.80, 0.90, 1.00, 1.05, 1.10],  // even distribution
    'mid-high': [1.00, 1.05, 1.05, 1.00, 0.98],  // slightly tip-stiff
    'high':     [1.20, 1.15, 1.00, 0.90, 0.82],  // stiff tip → softer butt (least kick)
  };

  const shape = kickShapes[profile.kickPoint] || kickShapes['mid'];

  // Apply tip/butt stiffness modifiers
  // tipStiffness 1.2 means 20% stiffer at tip than nominal
  const tipScale = profile.tipStiffness;
  const buttScale = profile.buttStiffness;

  const ei: [number, number, number, number, number] = [
    baseEI * shape[0] * tipScale,
    baseEI * shape[1] * (tipScale * 0.6 + 0.4),     // blend toward mid
    baseEI * shape[2],                                 // mid is baseline
    baseEI * shape[3] * (buttScale * 0.4 + 0.6),     // blend toward mid
    baseEI * shape[4] * buttScale,
  ];

  // Damping ratio from material
  const dampingRatio = material === 'steel' ? 0.012 : 0.020;

  // Mass distribution from total weight
  const tipMass = weight * 0.15;   // ~15% at tip (adapter + tip section)
  const buttMass = weight * 0.50;  // ~50% at butt (grip + butt section)

  // Torsional stiffness inversely from torque rating
  // torque 2.5° ≈ 1.8 N·m/rad, torque 5.5° ≈ 0.8 N·m/rad
  const torsionalStiffness = Math.max(0.5, 4.5 / profile.torque);

  return { ei, dampingRatio, tipMass, buttMass, torsionalStiffness };
}

// ── Swing Kinematics from Tempo/Transition ───────────────────────────────────

/**
 * Derive SwingKinematics from player tempo, transition, and club speed.
 *
 * This maps the existing Tempo/Transition enums to the continuous kinematic
 * parameters the ODE model needs.
 */
export function deriveKinematics(
  clubSpeedMph: number,
  tempo: Tempo,
  transition: Transition,
): SwingKinematics {
  // Club speed → hand speed (hand moves ~70% of club head speed)
  const clubSpeedMs = clubSpeedMph * 0.44704;
  const peakHandSpeed = clubSpeedMs * 0.70;

  // Tempo → peak timing
  const peakTimings: Record<Tempo, number> = {
    slow: 0.80,
    medium: 0.72,
    fast: 0.63,
  };

  // Tempo → release point (fast tempo tends to have later release)
  const releasePoints: Record<Tempo, number> = {
    slow: 0.50,
    medium: 0.60,
    fast: 0.70,
  };

  // Transition → force and release rate
  const transitionForces: Record<Transition, number> = {
    smooth: 0.30,
    medium: 0.55,
    quick: 0.85,
  };

  const releaseRates: Record<Transition, number> = {
    smooth: 18,
    medium: 24,
    quick: 32,
  };

  return {
    peakHandSpeed,
    peakTimingNorm: peakTimings[tempo],
    releasePointNorm: releasePoints[tempo],
    releaseRate: releaseRates[transition],
    transitionForce: transitionForces[transition],
  };
}

// ── High-Level API ───────────────────────────────────────────────────────────

/**
 * Compute shaft loading modifiers from shaft profile and swing characteristics.
 *
 * This is the main entry point for the EquipmentEngine. It:
 *   1. Converts ShaftProfile → EI profile
 *   2. Derives swing kinematics from tempo/transition
 *   3. Runs the 3-mass ODE simulation
 *   4. Converts delivery → launch/spin/speed/dispersion modifiers
 *
 * @param profile - Shaft DNA profile (from library)
 * @param weight - Shaft weight in grams
 * @param material - 'graphite' or 'steel'
 * @param length - Shaft length in inches
 * @param clubSpeedMph - Club speed in mph
 * @param tempo - Player tempo
 * @param transition - Player transition style
 * @param headMassKg - Club head mass (driver ~0.200, iron ~0.260)
 */
// Baseline profile: "avg" shaft — mid kick, 3.5° torque, 1.0 tip/butt, 65g graphite
const BASELINE_PROFILE = { kickPoint: 'mid', torque: 3.5, tipStiffness: 1.0, buttStiffness: 1.0 };
const BASELINE_WEIGHT = 65;
const BASELINE_MATERIAL = 'graphite' as const;
const BASELINE_LENGTH = 45.5;

// Cache baseline deliveries per (clubSpeed, tempo, transition, headMass) to avoid redundant ODE runs.
// Key format: "speed|tempo|transition|headMass"
const baselineCache = new Map<string, ShaftDelivery>();

const baselineEIProfile = shaftProfileToEI(BASELINE_PROFILE, BASELINE_WEIGHT, BASELINE_MATERIAL);

function getBaselineDelivery(
  clubSpeedMph: number,
  headMassKg: number,
): ShaftDelivery {
  const key = `${clubSpeedMph}|${headMassKg}`;
  let cached = baselineCache.get(key);
  if (cached) return cached;

  const kinematics = deriveKinematics(clubSpeedMph, 'medium', 'medium');
  cached = simulateShaftLoading(baselineEIProfile, kinematics, headMassKg, BASELINE_LENGTH);
  baselineCache.set(key, cached);
  return cached;
}

export function computeShaftLoading(
  profile: { kickPoint: string; torque: number; tipStiffness: number; buttStiffness: number },
  weight: number,
  material: 'graphite' | 'steel',
  length: number,
  clubSpeedMph: number,
  tempo: Tempo = 'medium',
  transition: Transition = 'medium',
  headMassKg: number = 0.200,
): ShaftLoadingResult {
  const eiProfile = shaftProfileToEI(profile, weight, material);
  const kinematics = deriveKinematics(clubSpeedMph, tempo, transition);
  const delivery = simulateShaftLoading(eiProfile, kinematics, headMassKg, length);
  const baseline = getBaselineDelivery(clubSpeedMph, headMassKg);
  return deliveryToModifiers(delivery, baseline, eiProfile, baselineEIProfile);
}
